/* iceberg-ui */
import { DonutChart } from '@auviknetworks/iceberg-ui';

/*  MUI  */
import {
    Alert,
    AlertTitle,
    Card,
    CardContent,
    Grid,
    Skeleton,
    TableCell,
    Typography
} from '@mui/material';

/*  Shared  */
import { LifecyclePicker } from 'application/Shared/Components/Pickers';
import { popoverContentsColumns } from 'application/Shared/Components/Recharts/PopoverContents';
import { ReportWrapper } from 'application/Shared/Components/Reports';
import { ManagedTable } from 'application/Shared/Components/Table';
import { RecentlyAddedAppsTable } from 'application/Shared/Components/Tables/RecentlyAddedAppsTable';
import { RecentlyAddedAppsTableContextProvider } from 'application/Shared/Components/Tables/RecentlyAddedAppsTableContext';
import { TooltipQ } from 'application/Shared/Components/Tooltips';
import { LifecycleStatus } from 'application/Shared/Constants';
import { Helpers, Route, SSOUtils } from 'application/Shared/Helpers';
import { TenantApp } from 'application/Shared/Requests';

/*  NPM  */
import { subMonths } from 'date-fns';

/* Flags */
import { INCIDENTS, hasFeature } from 'featureFlags/src';
import i18n from 'i18n';

/*  React  */
import React, { PureComponent } from 'react';
import { Trans, withTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { withRouter } from 'router/withRouter';

/* Internal Components */
import { ExternalAppsStatus } from './internal/ExternalAppsStatus';
import { SSOBreakdown } from './internal/SSOBreakdown';
import { SSOCoverage } from './internal/SSOCoverage';

/*  Constants  */
const oneMonthAgo = subMonths(new Date(), 1).getTime();

const renderScores = () => (
    <ReportWrapper
        startTime={oneMonthAgo}
        period="upToNow"
        type="qbr"
        reportSelection="QBRFirst"
        reportCheckbox="ScoreOverview"
        applicationOrCategorySelection="application"
        userOrGroupSelection="all"
        noLegend
        useCache
        customLoader={
            <Grid container spacing={3}>
                <Grid item lg={2} md={4} sm={6} xs={12}>
                    <Card variant="outlined" style={{ height: '141px' }}>
                        <Skeleton animation="wave" height={120} />
                    </Card>
                </Grid>
                <Grid item lg={2} md={4} sm={6} xs={12}>
                    <Card variant="outlined" style={{ height: '141px' }}>
                        <Skeleton animation="wave" height={120} />
                    </Card>
                </Grid>
                <Grid item lg={2} md={4} sm={6} xs={12}>
                    <Card variant="outlined" style={{ height: '141px' }}>
                        <Skeleton animation="wave" height={120} />
                    </Card>
                </Grid>
                <Grid item lg={2} md={4} sm={6} xs={12}>
                    <Card variant="outlined" style={{ height: '141px' }}>
                        <Skeleton animation="wave" height={120} />
                    </Card>
                </Grid>
                <Grid item lg={2} md={4} sm={6} xs={12}>
                    <Card variant="outlined" style={{ height: '141px' }}>
                        <Skeleton animation="wave" height={120} />
                    </Card>
                </Grid>
                <Grid item lg={2} md={4} sm={6} xs={12}>
                    <Card variant="outlined" style={{ height: '141px' }}>
                        <Skeleton animation="wave" height={120} />
                    </Card>
                </Grid>
            </Grid>
        }
        height={300}
    />
);

const headersLifecycle = [
    {
        renderHeader: <strong>{i18n.t('Common.lifecycleStage')}</strong>,
        width: '33%'
    },
    { renderHeader: <strong>{i18n.t('Common.total')}</strong>, width: '33%' },
    { renderHeader: <strong>{i18n.t('Common.percent')}</strong>, width: '33%' }
];

class Overview extends PureComponent {
    constructor(props) {
        super(props);
        this.state = {
            lifecycleChart: [],
            lifecycleRows: null,
            loaded: false,
            loadedAppData: false,
            ssoCounts: {},
            tenantApps: null
        };

        const clientId = props?.match?.params?.clientId;
        /* requests */
        this.TenantApp = new TenantApp(clientId);
    }

    componentDidMount() {
        this.load();
    }

    componentWillUnmount() {
        this.unmount = true;
        this.unload();
    }

    load = () => {
        this.loadAppData();

        if (!this.unmount) {
            this.setState({ loaded: true });
        }
    };

    unload = () => {
        this.unmount = true;
        this.TenantApp.cancel();
    };

    loadAppData = async () => {
        const { t } = this.props;

        if (this.unmount) {
            return;
        }

        const counts = {
            [LifecycleStatus.Approved]: 0,
            [LifecycleStatus.Decommissioned]: 0,
            [LifecycleStatus.Decommissioning]: 0,
            [LifecycleStatus.Evaluating]: 0,
            [LifecycleStatus.Implementing]: 0,
            [LifecycleStatus.NotConfigured]: 0,
            [LifecycleStatus.Unapproved]: 0
        };
        let total = 0;
        const lifecycleRows = [];
        const lifecycleChart = [];

        const ssoCounts = {
            googleSSODetected: 0,
            googleSSOSupport: 0,
            microsoftSSODetected: 0,
            microsoftSSOSupport: 0,
            SSOdetected: 0,
            SSOsupported: 0
        };

        try {
            const tenantApps = await this.TenantApp.getAll({
                includeApplication: true
            });

            tenantApps.forEach((d) => {
                const { status } = d;
                if (!Helpers.isset(counts, status)) {
                    return;
                }
                if (!d.ssoSupport) {
                    d.ssoSupport = SSOUtils.tenantAppGetSSOSupport(d); // eslint-disable-line no-param-reassign
                }

                // Increment counts based on conditions
                ssoCounts.microsoftSSODetected +=
                    d.microsoftSSODetected || d.azureSignInDetected ? 1 : 0;
                ssoCounts.googleSSODetected +=
                    d.googleSSODetected || d.googleSignInDetected ? 1 : 0;

                // Increment SSOdetected count if any SSO is detected
                ssoCounts.SSOdetected +=
                    d.microsoftSSODetected ||
                    d.azureSignInDetected ||
                    d.googleSSODetected ||
                    d.googleSignInDetected
                        ? 1
                        : 0;

                ssoCounts.SSOsupported += d.ssoSupport.length ? 1 : 0;

                counts[status] += 1;
                total += 1;
            });

            Object.keys(counts).forEach((status) => {
                const value = counts[status];

                const calculatedPercent =
                    total === 0 ? 0 : ((value / total) * 100).toFixed(2);

                lifecycleRows.push({
                    percent: calculatedPercent,
                    status,
                    total: value
                });
                lifecycleChart.push({ name: t(`Common.${status}`), value });
            });

            if (!this.unmount) {
                this.setState({
                    lifecycleChart,
                    lifecycleRows,
                    loadedAppData: true,
                    ssoCounts,
                    tenantApps
                });
            }
        } catch (e) {
            /* */
        }
    };

    renderAdoption = () => {
        const { t } = this.props;
        return (
            <Card variant="outlined">
                <CardContent>
                    <Typography variant="h5">
                        {t('Reports.UsageReport.adoption_and_usage')}
                    </Typography>
                    <hr />
                    <ReportWrapper
                        startTime={oneMonthAgo}
                        period="upToNow"
                        type="organizational"
                        disableUnclassified="false"
                        reportSelection="Usage"
                        reportCheckbox="VsAdoption"
                        applicationOrCategorySelection="application"
                        userOrGroupSelection="all"
                        noLegend
                        height={300}
                    />
                </CardContent>
            </Card>
        );
    };

    renderLifecycleTableRow = (row) => {
        const { loadedAppData } = this.state;
        return (
            <>
                <TableCell>
                    <LifecyclePicker visualOnly value={row.status} />
                </TableCell>
                <TableCell>
                    {loadedAppData ? row.total : <Skeleton animation="wave" />}
                </TableCell>
                <TableCell>
                    {loadedAppData ? (
                        `${row.percent}%`
                    ) : (
                        <Skeleton animation="wave" />
                    )}
                </TableCell>
            </>
        );
    };

    renderLifecycleTable = () => {
        const { lifecycleRows } = this.state;

        return (
            <ManagedTable
                headers={headersLifecycle}
                renderBody={this.renderLifecycleTableRow}
                rowHeight={10}
                rows={lifecycleRows}
                rowsPerPage={5}
                sortDirection="DESC"
                sortFieldType="number"
                sortKey="total"
            />
        );
    };

    renderLifecycleChart = () => {
        const { t } = this.props;
        const { loadedAppData, lifecycleChart } = this.state;
        if (!loadedAppData) {
            return (
                <Grid item xs={12} style={{ height: '250px' }}>
                    <Skeleton animation="wave" height={250} />
                </Grid>
            );
        }

        return (
            <Grid item xs={12} style={{ height: '250px' }}>
                <DonutChart
                    data={lifecycleChart}
                    emptyMessage={t('Common.noData')}
                    popoverContentsColumns={popoverContentsColumns}
                    responsiveContainerHeight={250}
                />
            </Grid>
        );
    };

    render() {
        const { match, t } = this.props;
        const { loaded } = this.state;

        if (!loaded) {
            return null;
        }

        const { clientId, partnerId } = match.params;
        const baseAdminRoute = Route.buildBaseAdminRoute({
            clientId,
            partnerId
        });

        return (
            <>
                <Alert
                    severity="warning"
                    icon={false}
                    sx={{ marginBottom: '16px' }}
                >
                    <AlertTitle>
                        {t('Alerts.page_retirement_notice_title')}
                    </AlertTitle>
                    <Trans
                        i18nKey="Alerts.page_retirement_notice"
                        components={{
                            DashboardLink: (
                                <Link
                                    className="link underline"
                                    data-trackid="adminApplicationsPageToBeRemovedAlertDashboardLink"
                                    to={baseAdminRoute}
                                >
                                    {t('Common.dashboard')}
                                </Link>
                            )
                        }}
                    />
                </Alert>
                <Grid
                    container
                    spacing={3}
                    data-testid="adminApplicationsPagesOverview"
                    data-trackid="adminApplicationsPagesOverview"
                    display="flex"
                >
                    <Grid item xs={12}>
                        {this.renderAdoption()}
                    </Grid>
                    <Grid item xs={12}>
                        {renderScores()}
                    </Grid>
                    {hasFeature(INCIDENTS) && (
                        <Grid item xs={12}>
                            <ExternalAppsStatus />
                        </Grid>
                    )}
                    <Grid item xs={12} lg={6}>
                        <Card variant="outlined" sx={{ height: '100%' }}>
                            <CardContent>
                                <Typography variant="h5">
                                    {t('AdminDigest.ssoBreakdown')}
                                    <TooltipQ
                                        text={t(
                                            'Tooltips.applications.ssoBreakdown'
                                        )}
                                    />
                                </Typography>
                                <hr />
                                <SSOBreakdown
                                    ssoCounts={this.state.ssoCounts}
                                />
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} lg={6}>
                        <Card variant="outlined" sx={{ height: '100%' }}>
                            <CardContent>
                                <Typography variant="h5">
                                    {t('AdminDigest.ssoCoverageUse')}
                                </Typography>
                                <hr />
                                <SSOCoverage
                                    tenantApps={this.state.tenantApps}
                                />
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} lg={6}>
                        <Card variant="outlined">
                            <CardContent>
                                <Typography variant="h5">
                                    {t(
                                        'AdminDigest.added_application_breakdown'
                                    )}
                                </Typography>
                                <hr />
                                <ReportWrapper
                                    startTime={oneMonthAgo}
                                    period="upToNow"
                                    type="applicationFunnel"
                                    reportSelection="All"
                                    reportCheckbox="Breakdown"
                                    applicationOrCategorySelection="application"
                                    userOrGroupSelection="all"
                                    height={128}
                                    noLegend
                                />
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} lg={6}>
                        <Card variant="outlined">
                            <CardContent>
                                <Typography variant="h5">
                                    {t(
                                        'AdminDigest.added_application_breakdown'
                                    )}
                                </Typography>
                                <hr />
                                <ReportWrapper
                                    startTime={oneMonthAgo}
                                    period="upToNow"
                                    type="applicationFunnel"
                                    reportSelection="All"
                                    reportCheckbox="OverviewBreakdown"
                                    applicationOrCategorySelection="application"
                                    userOrGroupSelection="all"
                                    height={128}
                                    noLegend
                                />
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} lg={6}>
                        <Card variant="outlined" style={{ height: '100%' }}>
                            <CardContent>
                                <Typography variant="h5">
                                    {t('Applications.by_lifecycle')}
                                </Typography>
                                <hr />
                                {this.renderLifecycleTable()}
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} lg={6}>
                        <Card variant="outlined" style={{ height: '100%' }}>
                            <CardContent>
                                <Typography variant="h5">
                                    {t('Applications.by_lifecycle')}
                                </Typography>
                                <hr />
                                {this.renderLifecycleChart()}
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12}>
                        <RecentlyAddedAppsTableContextProvider>
                            <RecentlyAddedAppsTable
                                fromPage={t(
                                    'ApplicationDashboard.applicationInsights'
                                )}
                            />
                        </RecentlyAddedAppsTableContextProvider>
                    </Grid>
                </Grid>
            </>
        );
    }
}

export default withRouter(withTranslation()(Overview));
